import React, { useEffect, useMemo, useRef, useState } from "react";
import winePlatterVideo from "../../assets/wine-platter.mp4";

/**
 * ============================================================
 * GUIDE — HELPER TEMPOREL
 * ============================================================
 *
 * Retourne true si le temps vidéo courant est dans l’intervalle.
 */
function inRange(t, start, end) {
  return t >= start && t <= end;
}

export default function WinePlatterVideoOverlay() {
  const videoRef = useRef(null);

  const [t, setT] = useState(0);
  const [duration, setDuration] = useState(12);
  const [ratio, setRatio] = useState(16 / 9);
  const [mapping, setMapping] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  /**
   * ============================================================
   * GUIDE — ÉTAT DE CARTE ACTIVE STABLE
   * ============================================================
   *
   * On ne veut pas que la carte disparaisse brièvement
   * entre deux phases temporelles. Donc :
   * - si une carte visible est trouvée => on l’affiche
   * - sinon on garde la dernière carte active
   *
   * Cela supprime l’effet de clignotement.
   */
  const [activeCardId, setActiveCardId] = useState(null);

  /**
   * ============================================================
   * GUIDE — DÉTECTION MOBILE
   * ============================================================
   *
   * Permet d'utiliser des positions dédiées sur petit écran
   * pour éviter que les cartes sortent du cadre vidéo.
   */
  useEffect(() => {
    const update = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /**
   * ============================================================
   * GUIDE — FETCH MAPPING PLATEAU VIN
   * ============================================================
   */
  useEffect(() => {
    fetch("https://marchedeluxe.ch/services/get_platter_mapping.php?type=wine")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.success === false) {
          console.error("Erreur mapping plateau cave:", data.error);
          setMapping({});
          return;
        }
        setMapping(data || {});
      })
      .catch((err) => {
        console.error("Erreur mapping plateau cave:", err);
        setMapping({});
      });
  }, []);

  /**
   * ============================================================
   * GUIDE — TIMELINE DES CARTES
   * ============================================================
   *
   * Desktop :
   * - positions plus aérées
   *
   * Mobile :
   * - positions recentrées pour rester DANS la vidéo
   * - slot gauche / droite moins extrêmes
   */
  const cards = useMemo(
    () => [
      {
        id: "w1",
        firstPhase: {
          start: 0.8,
          end: 2.1,
          style: { left: "49%", top: "24%" },
          mobileStyle: { left: "50%", top: "29%" },
        },
      },
      {
        id: "w2",
        firstPhase: {
          start: 1.7,
          end: 3.1,
          style: { left: "72%", top: "36%" },
          mobileStyle: { left: "65%", top: "40%" },
        },
      },
      {
        id: "w3",
        firstPhase: {
          start: 3.2,
          end: 4.8,
          style: { left: "67%", top: "66%" },
          mobileStyle: { left: "64%", top: "64%" },
        },
      },
      {
        id: "w4",
        firstPhase: {
          start: 4.7,
          end: 6.2,
          style: { left: "37%", top: "70%" },
          mobileStyle: { left: "40%", top: "66%" },
        },
      },
      {
        id: "w5",
        firstPhase: {
          start: 6.1,
          end: 7.7,
          style: { left: "15%", top: "52%" },
          mobileStyle: { left: "19%", top: "49%" },
        },
      },
    ],
    []
  );

  /**
   * ============================================================
   * GUIDE — TICK VIDÉO
   * ============================================================
   *
   * RAF pour un suivi très fluide du currentTime.
   */
  useEffect(() => {
    let raf = 0;

    const tick = () => {
      const v = videoRef.current;
      if (v) setT(v.currentTime || 0);
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  /**
   * ============================================================
   * GUIDE — META VIDÉO
   * ============================================================
   */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onMeta = () => {
      if (v.duration) setDuration(v.duration);
      if (v.videoWidth && v.videoHeight) {
        setRatio(v.videoWidth / v.videoHeight);
      }
    };

    v.addEventListener("loadedmetadata", onMeta);
    return () => v.removeEventListener("loadedmetadata", onMeta);
  }, []);

  const localT = duration > 0 ? t % duration : t;

  /**
   * ============================================================
   * GUIDE — RECHERCHE DE LA CARTE VISIBILE À CET INSTANT
   * ============================================================
   */
  const visibleCardId = useMemo(() => {
    for (const card of cards) {
      const product = mapping?.[card.id];
      if (!product) continue;

      const visible = inRange(
        localT,
        card.firstPhase.start,
        card.firstPhase.end
      );
      if (visible) return card.id;
    }

    return null;
  }, [cards, mapping, localT]);

  /**
   * ============================================================
   * GUIDE — STABILISATION ANTI-FLICKER
   * ============================================================
   *
   * Si on a trouvé une carte visible, on met à jour l'id actif.
   * Si on n'en trouve pas, on garde la précédente.
   *
   * Cela évite les micro-disparitions entre deux slots.
   */
  useEffect(() => {
    if (visibleCardId) {
      setActiveCardId(visibleCardId);
    }
  }, [visibleCardId]);

  /**
   * ============================================================
   * GUIDE — CARTE ACTIVE FINALE
   * ============================================================
   */
  const activeCard = useMemo(() => {
    if (!activeCardId) return null;

    const card = cards.find((c) => c.id === activeCardId);
    if (!card) return null;

    const product = mapping?.[card.id];
    if (!product) return null;

    return {
      ...card,
      product,
      appliedStyle: isMobile
        ? card.firstPhase.mobileStyle || card.firstPhase.style
        : card.firstPhase.style,
    };
  }, [activeCardId, cards, mapping, isMobile]);

  return (
    <div className="wine-pvo-wrap">
      <div className="wine-pvo-stage" style={{ aspectRatio: ratio }}>
        <div className="wine-pvo-glow" />

        <video
          ref={videoRef}
          src={winePlatterVideo}
          autoPlay
          muted
          loop
          playsInline
          className="wine-pvo-video"
        />

        <div className="wine-pvo-overlay">
          {activeCard ? (
            <div
              className="wine-pvo-card is-visible"
              style={activeCard.appliedStyle}
            >
              <div className="wine-pvo-card-kicker">Sélection cave</div>

              <div className="wine-pvo-name">
                {activeCard.product?.name || "Produit"}
              </div>

              <div className="wine-pvo-price">
                {activeCard.product?.displayPrice || ""}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <style jsx="true">{`
        .wine-pvo-wrap {
          width: 100%;
        }

        .wine-pvo-stage {
          position: relative;
          width: min(100%, 1000px);
          margin: 0 auto;
          border-radius: 22px;
          overflow: hidden;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.82) 0%,
            rgba(248, 242, 234, 0.92) 100%
          );
        }

        .wine-pvo-glow {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(
            circle,
            rgba(118, 20, 39, 0.12) 0%,
            rgba(118, 20, 39, 0.05) 34%,
            rgba(118, 20, 39, 0) 70%
          );
          filter: blur(18px);
          z-index: 0;
        }

        .wine-pvo-video {
          position: relative;
          z-index: 1;
          width: 100%;
          height: 100%;
          display: block;
          object-fit: contain;
          background: transparent;
        }

        .wine-pvo-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          pointer-events: none;
        }

        /**
         * ========================================================
         * GUIDE — CARTE UNIQUE STABLE
         * ========================================================
         *
         * Plus de keyframe agressive :
         * - juste une présence stable
         * - légère animation d'entrée
         * - pas de disparition parasite
         */
        .wine-pvo-card {
          position: absolute;
          transform: translate(-50%, -74%);
          min-width: 156px;
          max-width: 240px;
          padding: 10px 14px 11px;
          border-radius: 16px;
          backdrop-filter: blur(12px);
          background: rgba(255, 255, 255, 0.78);
          box-shadow: 0 10px 26px rgba(0, 0, 0, 0.12);
          border: 1px solid rgba(117, 88, 57, 0.08);
          opacity: 1;
          transition: left 220ms ease, top 220ms ease, opacity 180ms ease,
            transform 180ms ease;
        }

        .wine-pvo-card.is-visible {
          opacity: 1;
        }

        .wine-pvo-card-kicker {
          color: rgba(92, 62, 39, 0.68);
          font-family: "Inter", sans-serif;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          margin-bottom: 5px;
        }

        .wine-pvo-name {
          color: #241a13;
          font-family: "Playfair Display", serif;
          font-size: 15px;
          font-weight: 600;
          line-height: 1.22;
        }

        .wine-pvo-price {
          margin-top: 5px;
          color: rgba(36, 26, 19, 0.72);
          font-family: "Inter", sans-serif;
          font-size: 12px;
          font-weight: 600;
          line-height: 1.3;
        }

        @media (max-width: 640px) {
          .wine-pvo-stage {
            border-radius: 18px;
          }

          .wine-pvo-card {
            min-width: 118px;
            max-width: 158px;
            padding: 8px 10px 9px;
            border-radius: 14px;
            transform: translate(-50%, -66%);
          }

          .wine-pvo-card-kicker {
            font-size: 8px;
            margin-bottom: 4px;
          }

          .wine-pvo-name {
            font-size: 11px;
            line-height: 1.22;
          }

          .wine-pvo-price {
            margin-top: 4px;
            font-size: 10px;
          }
        }
      `}</style>
    </div>
  );
}
